<template>
  <aside
    class="h-full bg-dark-secondary border-r border-dark flex flex-col select-none transition-all duration-300 ease-out relative overflow-x-hidden"
    :class="collapsed ? 'w-12' : 'w-[200px]'"
  >
    <!-- HEADER -->
    <div class="h-[40px] border-b border-dark flex items-center bg-dark-tertiary relative">
      <div
        class="absolute left-3 transition-opacity duration-300"
        :class="collapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'"
      >
        <span class="text-xs uppercase font-semibold text-dark-primary tracking-wide whitespace-nowrap">
          Navigation
        </span>
      </div>
      <button
        @click="toggle"
        class="p-1 text-dark-secondary hover:text-dark-primary transition z-10"
        :class="collapsed ? 'mx-auto' : 'absolute right-2'"
      >
        <ChevronRight v-if="collapsed" class="w-5 h-5" />
        <ChevronLeft  v-else           class="w-5 h-5" />
      </button>
    </div>

    <!-- NAV ITEMS -->
    <div class="flex-1 py-2 space-y-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
      <button
        @click="hasBackend && router.push({ name: 'home' })"
        class="sidebar-item"
        :class="[
          { active: hasBackend && (currentRoute === 'home' || currentRoute === 'dashboard') },
          { disabled: !hasBackend }
        ]"
        :title="collapsed ? 'Dashboard' : (!hasBackend ? 'Configure a backend in Settings first' : '')"
      >
        <div class="sidebar-item-content">
          <LayoutDashboard class="sidebar-icon" />
          <span class="sidebar-text font-medium" :class="textClass">Dashboard</span>
        </div>
      </button>

      <button
        @click="hasBackend && router.push({ name: 'sessions' })"
        class="sidebar-item"
        :class="[
          { active: hasBackend && currentRoute === 'sessions' },
          { disabled: !hasBackend }
        ]"
        :title="collapsed ? 'Sessions' : (!hasBackend ? 'Configure a backend in Settings first' : '')"
      >
        <div class="sidebar-item-content">
          <List class="sidebar-icon" />
          <span class="sidebar-text font-medium" :class="textClass">Sessions</span>
        </div>
      </button>

      <button
        @click="hasBackend && router.push({ name: 'recordings' })"
        class="sidebar-item"
        :class="[
          { active: hasBackend && currentRoute === 'recordings' },
          { disabled: !hasBackend }
        ]"
        :title="collapsed ? 'Recordings' : (!hasBackend ? 'Configure a backend in Settings first' : '')"
      >
        <div class="sidebar-item-content">
          <HardDrive class="sidebar-icon" />
          <span class="sidebar-text font-medium" :class="textClass">Recordings</span>
        </div>
      </button>
    </div>

    <!-- BOTTOM: Settings -->
    <div class="py-2" style="border-top: 1px solid var(--border-color)">
      <button
        @click="router.push({ name: 'settings' })"
        class="sidebar-item"
        :class="{ active: currentRoute === 'settings' }"
        :title="collapsed ? 'Settings' : ''"
      >
        <div class="sidebar-item-content">
          <SettingsIcon class="sidebar-icon" />
          <span class="sidebar-text font-medium" :class="textClass">Settings</span>
        </div>
      </button>
    </div>

  </aside>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/authStore";
import {
  ChevronRight, ChevronLeft,
  LayoutDashboard, HardDrive, List,
  Settings as SettingsIcon,
} from "lucide-vue-next";

const router       = useRouter();
const authStore    = useAuthStore();
const collapsed    = ref(false);
const currentRoute = computed(() => router.currentRoute.value.name);
const hasBackend   = computed(() => authStore.hasBackend);
const textClass    = computed(() =>
  collapsed.value
    ? "opacity-0 translate-x-[-10px] pointer-events-none"
    : "opacity-100 translate-x-0"
);

function toggle() { collapsed.value = !collapsed.value; }
</script>

<style scoped>
.sidebar-item {
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  color: var(--text-secondary);
  font-size: 13px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  padding: 10px 0;
  overflow: hidden;
}
.sidebar-item:hover { color: var(--text-primary); background: var(--hover-bg); }
.sidebar-item.active {
  color: var(--text-primary);
  background: rgba(var(--accent-rgb), 0.15);
  border-left: 3px solid var(--accent);
}
.sidebar-item.disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.sidebar-item.disabled:hover {
  background: transparent;
  color: var(--text-secondary);
}
.sidebar-item-content {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  padding-left: 14px;
}
.sidebar-icon { position: absolute; left: 14px; width: 20px; height: 20px; flex-shrink: 0; }
.sidebar-text {
  margin-left: 32px;
  white-space: nowrap;
  transition: opacity 0.3s ease, transform 0.3s ease;
  will-change: opacity, transform;
}
.custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
.custom-scrollbar::-webkit-scrollbar       { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
</style>